import colors from '../settings/colors'
import fontFaces from '../settings/fontfaces'
import fontWeights from '../settings/fontweights'
import fontSizes from '../settings/fontsizes'
import { hexToRgba } from '../tools/colorMapper'
import { CONTEXT_ROOT } from '../../utils'

const assetsPath = `${CONTEXT_ROOT}/assets`

export default {
  colors: {
    common: {
      html: {
        text: {
          default: colors.purple.default
        },
        bg: {
          default: colors.blue.default
        }
      },
      body: {
        text: {
          default: colors.purple.default
        },
        bg: {
          default: colors.neutro.light
        }
      },
      tap: {
        default: hexToRgba(colors.blue.default, '.1')
      },
      selection: {
        text: { default: colors.purple.lighter },
        bg: { default: colors.blue.lightest }
      },
      separator: {
        default: colors.purple.lightest
      },
      headings: {
        h1: colors.purple.default,
        h2: colors.purple.lighter
      },
      paragraph: {
        default: colors.purple.default
      },
      utility: {
        error: {
          default: colors.utility.red.default,
          lightest: colors.utility.red.lightest
        },
        warning: {
          default: colors.utility.yellow.default,
          lightest: colors.utility.yellow.lightest
        },
        info: {
          default: colors.utility.blue.default,
          lightest: colors.utility.blue.lightest
        },
        success: {
          default: colors.utility.green.default,
          lightest: colors.utility.green.lightest
        }
      },
      text: colors.purple.default
    },
    label: {
      default: colors.blue.default,
      disabled: colors.purple.lighter,
      sellaAuth: colors.sella.light
    },
    input: {
      bg: {
        default: colors.neutro.light
      },
      border: {
        default: colors.purple.lightest,
        active: colors.blue.default,
        blink: colors.purple.lighter,
        sella: colors.sella.light
      },
      option: {
        active: colors.blue.lighter,
        hover: colors.blue.lightest
      },
      text: {
        default: colors.purple.default,
        disabled: colors.purple.default
      },
      placeholder: {
        default: colors.purple.lighter
      }
    },
    spinner: {
      dots: {
        transparent: hexToRgba(colors.blue.lightest, '.2'),
        opaque: hexToRgba(colors.blue.lightest, '1')
      },
      circle: {
        default: colors.blue.default
      },
      bg: {
        transparent: hexToRgba(colors.blue.darker, '.8'),
        opaque: colors.blue.default
      }
    },
    header: {
      bg: colors.gray.default,
      children: {
        bg: colors.gray.default
      }
    },
    flowbar: {
      bullet: {
        default: colors.blue.lightest,
        done: colors.blue.default
      },
      line: {
        default: colors.blue.lightest,
        done: colors.blue.default
      },
      item: {
        default: colors.gray.light,
        done: colors.gray.default
      }
    },
    title: colors.purple.darker,
    subtitle: colors.purple.default,
    footer: {
      bg: colors.purple.darkest,
      text: hexToRgba(colors.floral.default, '.8'),
      copyright: hexToRgba(colors.floral.default, '.2'),
      label: colors.floral.default,
      link: colors.floral.default,
      linkHover: colors.floral.darker,
      linkAlt: colors.blue.default,
      linkAltHover: colors.blue.dark
    },
    flag: {
      default: colors.purple.lightest,
      disabled: colors.purple.lighter,
      checked: colors.blue.default,
      bg: colors.neutro.light
    },
    button: {
      text: {
        default: colors.blue.default,
        primary: colors.utility.white.default,
        disabled: colors.blue.default,
        sella: colors.sella.dark,
        active: colors.utility.white.default
      },
      bg: {
        default: colors.neutro.light,
        primary: colors.utility.green.dark,
        sella: colors.sella.light,
        active: colors.utility.green.lightest,
        delete: colors.utility.red.default
      },
      border: {
        primary: colors.utility.green.dark,
        default: colors.blue.default,
        disabled: colors.gray.light,
        active: colors.utility.green.lightest
      }
    },
    link: {
      text: {
        default: colors.blue.dark,
        hover: colors.blue.default
      }
    },
    widget: {
      groupTitle: {
        default: colors.purple.darker
      }
    },
    fileUpload: {
      border: {
        default: colors.purple.lightest,
        focus: colors.blue.light,
        error: colors.utility.red.default
      },
      bg: colors.neutro.light,
      text: colors.purple.default
    },
    datepicker: {
      header: {
        text: colors.floral.default,
        bg: colors.blue.default
      },
      day: {
        text: {
          default: colors.purple.default,
          hover: colors.neutro.light,
          keyboard: colors.neutro.light,
          selected: colors.neutro.light
        },
        bg: {
          hover: colors.floral.dark,
          keyboard: colors.blue.light,
          selected: colors.blue.default
        }
      },
      arrow: colors.blue.lightest
    },
    modal: {
      title: colors.floral.default,
      overlay: colors.blue.darker,
      bg: colors.neutro.light,
      closeButton: colors.blue.lightest,
      selfie: {
        contentBg: colors.neutro.light
      }
    },
    auth: {
      sella: {
        bg: colors.sella.dark,
        title: colors.sella.light,
        label: colors.sella.light,
        inputBorder: '#f1f2ef',
        off: colors.neutro.light,
        link: {
          default: colors.utility.red.default,
          hover: colors.utility.red.dark
        }
      }
    },
    requestList: {
      header: {
        text: colors.floral.default,
        bg: colors.blue.default,
        border: colors.blue.default
      },
      content: {
        text: colors.purple.default,
        bg: colors.neutro.light
      }
    },
    chatbot: {
      default: colors.blue.default
    }
  },
  spacings: {
    default: `${0.5}rem`,
    x2: `${0.5 * 2}rem`,
    x3: `${0.5 * 3}rem`,
    x4: `${0.5 * 4}rem`,
    x5: `${0.5 * 5}rem`,
    x6: `${0.5 * 6}rem`,
    x7: `${0.5 * 7}rem`,
    header: {
      height: {
        default: '116px',
        /* medium: '75px',  */
        noFlowbar: '75px',
        noHeader: '75px'
      },
      children: {
        height: {
          medium: '75px'
        },
        paddingTop: { default: 0 },
        marginTop: {
          default: 0,
          noHeader: 0,
          img: 'unset'
        }
      },
      logo: {
        maxWidth: 'auto',
        width: '169px',
        height: '32px',
        /* width: '135px',
        height: '24px', */
        margin: {
          default: '1rem auto 0',
          /* medium: '1.4rem 2rem', */
          /* medium: '2rem 2rem', */
          centered: '1rem auto',
          img: 'unset'
        },
        /* position: 'absolute', */
        backgroundSize: 'contain',
        backgroundPosition: 'center center'
      }
    },
    flowbar: {
      paddingTop: {
        default: '8px'
      }
    },
    input: {
      padding: {
        top: {
          default: '.3rem'
        }
      },
      selectboxHeight: '37px'
    },
    radioImage: {
      selfie: {
        default: '54%',
        small: '54%'
      },
      virtualCard: {
        default: '51%',
        small: '51%'
      }
    },
    thankYouImg: {
      backgroundPosition: {
        default: 'center',
        smaller: 'center'
      }
    }
  },
  images: {
    logoSella: `url('${assetsPath}/images/logosella.svg')`,
    notfound: `url('${assetsPath}/images/404.png')`,
    welcome: `url('${assetsPath}/images/thank_you.svg')`,
    header: `url('${assetsPath}/images/hype_logo_header.svg')`,
    //header: `url('${assetsPath}/images/hype_logo_header.png')`,
    footer: `url('${assetsPath}/images/hype_logo_footer.svg')`,
    selectTriangle: `url('${assetsPath}/images/select_triangle.svg') 99% 56% no-repeat ${
      colors.neutro.light
    }`,
    closeIcon: `url('${assetsPath}/images/close_icon.svg')`,
    closeIconSella: `url('${assetsPath}/images/close_icon_sella.svg')`,
    rsaToken: `url('${assetsPath}/images/token.svg')`,
    sellaItLogo: `url('${assetsPath}/images/sella_it_logo.svg')`,
    phisicalCard: `url('${assetsPath}/images/card_physical.svg')`,
    phisicalCardSelected: `url('${assetsPath}/images/card_physical_selected.svg')`,
    virtualCard: `url('${assetsPath}/images/card_virtual.svg')`,
    virtualCardSelected: `url('${assetsPath}/images/card_virtual_selected.svg')`,
    selfie: `url('${assetsPath}/images/selfie.svg')`,
    selfieSelected: `url('${assetsPath}/images/selfie_selected.svg')`,
    bonifico: `url('${assetsPath}/images/bonifico.svg')`,
    bonificoSelected: `url('${assetsPath}/images/bonifico_selected.svg')`,
    checkboxTick:
      'url(data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyB3aWR0aD0iMTJweCIgaGVpZ2h0PSI5cHgiIHZpZXdCb3g9IjAgMCAxMiA5IiB2ZXJzaW9uPSIxLjEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiPgogICAgPCEtLSBHZW5lcmF0b3I6IFNrZXRjaCA0MC4zICgzMzgzOSkgLSBodHRwOi8vd3d3LmJvaGVtaWFuY29kaW5nLmNvbS9za2V0Y2ggLS0+CiAgICA8dGl0bGU+Y2hlY2tib3hfbWFyazwvdGl0bGU+CiAgICA8ZGVzYz5DcmVhdGVkIHdpdGggU2tldGNoLjwvZGVzYz4KICAgIDxkZWZzPjwvZGVmcz4KICAgIDxnIGlkPSJTdGVwLTMiIHN0cm9rZT0ibm9uZSIgc3Ryb2tlLXdpZHRoPSIxIiBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPgogICAgICAgIDxnIGlkPSJTdGVwLS0tMyIgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoLTQzNS4wMDAwMDAsIC03MjQuMDAwMDAwKSIgZmlsbD0iI0ZFRkFGMiI+CiAgICAgICAgICAgIDxnIGlkPSJDb250ZW50IiB0cmFuc2Zvcm09InRyYW5zbGF0ZSg0MzEuMDAwMDAwLCAxMzEuMDAwMDAwKSI+CiAgICAgICAgICAgICAgICA8ZyBpZD0iRGF0aS1TZW5zaWJpbGkiIHRyYW5zZm9ybT0idHJhbnNsYXRlKDAuMDAwMDAwLCA1ODguMDAwMDAwKSI+CiAgICAgICAgICAgICAgICAgICAgPGcgaWQ9Ikdyb3VwLTQiPgogICAgICAgICAgICAgICAgICAgICAgICA8cGF0aCBkPSJNNy40MTUzMzk0OSwxMy42NzQ1Njg2IEM3LjgwMjc2MTQyLDE0LjA5ODU0MjYgOC40NjY4NzQ4NSwxNC4xMDk4MSA4Ljg2ODQ1NTk4LDEzLjY5OTIyMjMgTDE1LjcxNDkwNDQsNi42OTkyMjIyNSBDMTYuMTAxMDc0MSw2LjMwNDM5MTQ4IDE2LjA5NDA1Myw1LjY3MTI2NTQzIDE1LjY5OTIyMjMsNS4yODUwOTU2NCBDMTUuMzA0MzkxNSw0Ljg5ODkyNTg2IDE0LjY3MTI2NTQsNC45MDU5NDY5NyAxNC4yODUwOTU2LDUuMzAwNzc3NzUgTDcuNDM4NjQ3MjcsMTIuMzAwNzc3NyBMOC44OTE3NjM3NiwxMi4zMjU0MzE0IEw1LjczODIxMjEzLDguODc0MzUxNzYgQzUuMzY1NjU4MTYsOC40NjY2NDg0NSA0LjczMzEzNDY2LDguNDM4MTU0MjkgNC4zMjU0MzEzNiw4LjgxMDcwODI2IEMzLjkxNzcyODA2LDkuMTgzMjYyMjMgMy44ODkyMzM5LDkuODE1Nzg1NzMgNC4yNjE3ODc4NywxMC4yMjM0ODkgTDcuNDE1MzM5NDksMTMuNjc0NTY4NiBaIiBpZD0iY2hlY2tib3hfbWFyayI+PC9wYXRoPgogICAgICAgICAgICAgICAgICAgIDwvZz4KICAgICAgICAgICAgICAgIDwvZz4KICAgICAgICAgICAgPC9nPgogICAgICAgIDwvZz4KICAgIDwvZz4KPC9zdmc+)',
    documents: {
      tris: `url('${assetsPath}/images/tris_documenti.svg')`,
      patenteFronte: `url('${assetsPath}/images/patente_fronte.svg')`,
      patenteRetro: `url('${assetsPath}/images/patente_retro.svg')`,
      cartaIdentitaFronte: `url('${assetsPath}/images/carta_identita_fronte.svg')`,
      cartaIdentitaRetro: `url('${assetsPath}/images/carta_identita_retro.svg')`,
      passaportoFronte: `url('${assetsPath}/images/passaporto_fronte.svg')`,
      passaportoRetro: `url('${assetsPath}/images/passaporto_retro.svg')`,
      selfieSelected: `url('${assetsPath}/images/selfie_selected.svg')`,
      uploaderLoading: `url('${assetsPath}/images/spinner.png')`
    },
    whatYouNeed: {
      documents: `url('${assetsPath}/images/wyn_documents.svg')`,
      phone: `url('${assetsPath}/images/wyn_phone.svg')`,
      secure: `url('${assetsPath}/images/wyn_secure.svg')`
    }
  },
  layouts: {
    header: {
      width: '118px',
      height: '44px'
    },
    button: {
      borderRadius: '4px'
    }
  },
  fontFamilies: {
    default: [
      fontFaces.muli.light,
      fontFaces.muli.normal,
      fontFaces.muli.semibold,
      fontFaces.muli.bold,
      'sans-serif'
    ],
    alternate: [
      fontFaces.muli.light,
      fontFaces.muli.normal,
      fontFaces.muli.semibold,
      fontFaces.muli.bold,
      'sans-serif'
    ]
  },
  fontWeights: {
    body: {
      default: fontWeights.normal
    },
    b: {
      default: fontWeights.semibold
    },
    flowbar: {
      default: fontWeights.semibold
    },
    datepicker: {
      default: fontWeights.normal
    },
    image: {
      default: fontWeights.light
    },
    radio: {
      title: fontWeights.semibold,
      subtitle: fontWeights.normal
    },
    requestList: {
      header: fontWeights.semibold
    },
    result: {
      title: fontWeights.bold
    },
    title: {
      default: fontWeights.normal
    },
    subtitle: {
      default: fontWeights.normal,
      sella: fontWeights.normal
    },
    widget: {
      group: fontWeights.semibold
    },
    label: {
      default: fontWeights.bold
    },
    input: {
      default: fontWeights.semibold,
      error: fontWeights.normal
    }
  },
  fontSizes: {
    ...fontSizes,
    flowbar: {
      //default: '0.875rem'
      default: '0.6875rem'
    },
    input: {
      default: fontSizes.normal,
      textIndent: '0',
      lineHeight: '1.5rem'
    }
  },
  textTransforms: {
    input: {
      placeholder: 'none'
    }
  },
  textDecorations: {
    link: {
      default: 'none',
      hover: 'none'
    }
  }
}



// WEBPACK FOOTER //
// ./styles/themes/pos.js